/**
 * Message Encoding Utility
 * Encodes/decodes chat messages to prevent storing plain text in database
 */

/**
 * Encode a message before storing in database
 * Uses base64 encoding with a prefix to identify encoded messages
 */
export function encodeMessage(content: string): string {
  if (!content) return content;
  
  try {
    // Use encodeURIComponent to handle UTF-8 properly, then base64 encode
    const encoded = btoa(encodeURIComponent(content));
    // Add prefix to identify encoded messages
    return `ENC:${encoded}`;
  } catch (error) {
    console.error('Error encoding message:', error);
    // If encoding fails, return original (shouldn't happen)
    return content;
  }
}

/**
 * Decode a message after retrieving from database
 * Handles both encoded (with ENC: prefix) and plain text (for backward compatibility)
 */
export function decodeMessage(encodedContent: string): string {
  if (!encodedContent) return encodedContent;
  
  // Check if message is encoded (has ENC: prefix)
  if (encodedContent.startsWith('ENC:')) {
    try {
      const base64Content = encodedContent.substring(4); // Remove 'ENC:' prefix
      
      // Try plain base64 decode first (handles most cases including simple text)
      try {
        const decoded = atob(base64Content);
        // If decoded contains URI-encoded characters, decode them
        try {
          return decodeURIComponent(decoded);
        } catch {
          // If URI decode fails, return plain decoded (works for simple text)
          return decoded;
        }
      } catch (plainError) {
        // If plain decode fails, try with URI component wrapper
        try {
          return decodeURIComponent(atob(base64Content));
        } catch (uriError) {
          console.error('Error decoding message (all methods failed):', uriError, 'Content:', encodedContent);
          return encodedContent;
        }
      }
    } catch (error) {
      console.error('Error decoding message:', error, 'Content:', encodedContent);
      // If decoding fails, return original (fallback)
      return encodedContent;
    }
  }
  
  // If no prefix, assume it's plain text (backward compatibility with existing messages)
  return encodedContent;
}
